import React, { FC } from 'react';
import { Paper } from '@mui/material';
import take from 'lodash/take';
import { nanoid } from 'nanoid';
import { useSelector } from 'react-redux';

import { ILabourAllocationDTO } from 'src/api/dtos';
import { ClockIcon, DepartmentIcon, SuitcaseIcon } from 'src/shared/components';
import { DATE_TIME_FORMAT } from 'src/shared/constants';
import { FeaturesEnums } from 'src/shared/enums';
import { ComponentWithFeatures } from 'src/shared/hocs';
import { useFeatures } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { formatTime } from 'src/shared/utils';
import { RootState } from 'src/store/types';
import { getTimeFormat } from 'src/store/user/user.selectors';

interface Props {
  labourAllocations: ILabourAllocationDTO[];
}

export const CalendarAllocationsInfo: FC<Props> = ({ labourAllocations }) => {
  const { t } = useTranslate();
  const { settings } = useSelector((state: RootState) => state.user);
  const timeFormat = useSelector(getTimeFormat);

  const hasJobCosting = useFeatures(FeaturesEnums.JobCosting);
  const Icon = hasJobCosting ? SuitcaseIcon : DepartmentIcon;

  const label = hasJobCosting ? t('calendar.scheduledJobs') : t('calendar.scheduledDepartments');
  const emptyText = hasJobCosting ? t('calendar.noScheduledJobsToShow') : t('calendar.noScheduledDepartmentsToShow');

  return (
    <div className="d-flex align-items-start mb-3 w-100">
      <Icon color="#6C757D" size={20} className="me-2" />
      <div className="w-100">
        <span className="fw-500 me-1">{label}:</span>
        <div className="d-block">
          {!labourAllocations.length ? (
            <label className="text-muted fw-400 small mt-1">{emptyText}</label>
          ) : (
            labourAllocations.map((allocation, index) => {
              const { startTime, endTime, departmentDescription, jobLevels } = allocation;
              const jobLevelsList = hasJobCosting ? Object.entries(jobLevels) : [];

              const isJobLevelEmpty = !jobLevelsList.length || jobLevelsList.every(([, value]) => !value?.length);

              const formattedStartTime = startTime ? formatTime(startTime, timeFormat, DATE_TIME_FORMAT) : undefined;
              const formattedEndTime = endTime ? formatTime(endTime, timeFormat, DATE_TIME_FORMAT) : undefined;

              return (
                <Paper variant="outlined" key={`${startTime}-${index}`} className="bg-inherit p-3 mt-3 small w-75">
                  <div className="d-flex align-items-center mb-3">
                    <ClockIcon color="#6C757D" size={16} className="me-2" />
                    <span>
                      {formattedStartTime} - {formattedEndTime}
                    </span>
                  </div>
                  <ComponentWithFeatures features={FeaturesEnums.JobCosting}>
                    {!isJobLevelEmpty && (
                      <div className="d-flex">
                        <SuitcaseIcon color="#6C757D" size={16} className="me-2" />
                        <div>
                          {take(jobLevelsList, settings?.numberOfJobLevels || jobLevelsList.length).map(
                            ([key, value]) => {
                              return (
                                <p key={nanoid()} className="mb-2" style={{ lineHeight: 1.4 }}>
                                  {key}: {value}
                                </p>
                              );
                            },
                          )}
                        </div>
                      </div>
                    )}
                  </ComponentWithFeatures>
                  <ComponentWithFeatures features={FeaturesEnums.DepartmentAllocation}>
                    <div className="d-flex align-items-center">
                      <DepartmentIcon color="#6C757D" size={16} className="me-2" />
                      <div>
                        <span className="me-1">
                          {t('calendar.department')}: {departmentDescription}
                        </span>
                      </div>
                    </div>
                  </ComponentWithFeatures>
                </Paper>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
